"use client";

import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  MenuItem,
  FormControl,
  Select,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  InputLabel
} from '@mui/material';

import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { apiClient } from '@/services/api';
import UserAvatar from '@/components/common/UserAvatar';

import MenuBookIcon from "@mui/icons-material/MenuBook";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PsychologyIcon from "@mui/icons-material/Psychology";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

interface Project {
  id: number;
  project_name: string;
  project_code: string;
  description?: string;
  user_id: number;
  org_id: number;
  report_template_id?: number;
  industry_type?: string;
  client_level?: string;
  evaluation_type?: string;
  status: "new" | "archived_mentor";
  contract_year?: number;
  dify_dataset_id?: string;
  project_type?: string;
  created_at: string;
  updated_at: string;
}

interface NavbarProps {
  // 暂时不需要props，因为组件内部自己管理状态
}

export default function Navbar({ }: NavbarProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [currentProjectType, setCurrentProjectType] = useState<string>("");
  const [helpMenuAnchor, setHelpMenuAnchor] = useState<HTMLElement | null>(null);
    const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Only show project details on relevant pages
  // 判断是否在项目详情页面（URL包含/projects/数字）
  const isProjectDetailPage = /^\/projects\/\d+/.test(location.pathname);

  // 从URL获取当前项目ID，然后从projects数组中查找项目信息
  const currentProjectIdFromPath = /^\/projects\/(\d+)/.exec(location.pathname)?.[1] || '';
  const currentProjectId = selectedProject || currentProjectIdFromPath;
  const currentProject: Project | null =
    projects.find((p: Project) => p.id.toString() === currentProjectId) || null;
  const isArchived = currentProject?.status === "archived_mentor";

  // 确保在项目详情页面时，currentProjectId不会为空
  const finalProjectId = isProjectDetailPage ? currentProjectId || currentProjectIdFromPath : '';

  // 截断项目名称的辅助函数
  const truncateProjectName = (
    projectCode: string,
    projectName: string,
    maxLength = 30,
  ) => {
    const fullName = `${projectCode}-${projectName}`;
    if (fullName.length <= maxLength) {
      return fullName;
    }
    // 保留项目编码，截断项目名称
    const codeLength = projectCode.length + 1; // +1 for the dash
    const remainingLength = maxLength - codeLength - 3; // -3 for "..."
    if (remainingLength <= 0) {
      return `${projectCode}-...`;
    }
    return `${projectCode}-${projectName.substring(0, remainingLength)}...`;
  };

  let projectMenuItems: React.ReactNode[] = [];
  if (isArchived && currentProject) {
    projectMenuItems = [
      <MenuItem
        key={currentProject.id}
        value={currentProject.id}
        sx={{
          maxWidth: 400,
          "& .MuiTypography-root": {
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
        }}
      >
        {truncateProjectName(
          currentProject.project_code,
          currentProject.project_name,
        )}
      </MenuItem>,
    ];
  } else if (!isArchived) {
    projectMenuItems = projects.map((project: Project) => (
      <MenuItem
        key={project.id}
        value={project.id}
        sx={{
          maxWidth: 400,
          "& .MuiTypography-root": {
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
        }}
      >
        {truncateProjectName(project.project_code, project.project_name)}
      </MenuItem>
    ));
  }

  useEffect(() => {
    const fetchProjects = async () => {
      console.log("Navbar useEffect: Fetching projects..."); // 新增日志
      if (user?.id) {
        try {
          // 先获取当前项目信息，确定项目类型
          const currentProjectIdFromPath = /^\/projects\/(\d+)/.exec(location.pathname)?.[1];
          let projectType = ""; // 初始为空，等待API返回

          if (currentProjectIdFromPath) {
            // 获取当前项目信息来确定类型
            try {
              console.log("Fetching current project:", currentProjectIdFromPath);
              const currentProjectResponse = await apiClient.get(`/projects/${currentProjectIdFromPath}`);
              console.log("Current project response:", currentProjectResponse);
              if (currentProjectResponse.success && currentProjectResponse.data) {
                projectType = currentProjectResponse.data.project_type || "key_project";
                setCurrentProjectType(projectType); // 更新状态
                console.log("Current project type:", projectType);
                console.log("Current project data:", currentProjectResponse.data);
              }
            } catch (error) {
              console.log("Could not fetch current project, using default type:", error);
              projectType = "key_project"; // 如果无法获取项目信息，默认为重点项目
              setCurrentProjectType(projectType);
            }
          } else {
            // 不在项目页面，设置默认类型
            projectType = "key_project";
            setCurrentProjectType(projectType);
          }

          // 根据项目类型获取对应的项目列表
          let response;
          console.log("About to fetch projects for type:", projectType);
          if (projectType === "performance_target") {
            // 获取绩效目标申报表
            console.log("Fetching performance targets");
            response = await apiClient.get("/projects/performance-targets?page_size=100");
          } else if (projectType === "department_overall") {
            // 获取部门整体目标
            console.log("Fetching department overall targets");
            response = await apiClient.get("/projects/?page_size=100&project_type=department_overall");
          } else if (projectType === "monitoring_target") {
            // 获取事中监控项目
            console.log("Fetching monitoring targets");
            response = await apiClient.get("/projects/?page_size=100&project_type=monitoring_target");
          } else if (projectType === "key_project") {
            // 获取重点项目
            console.log("Fetching key projects");
            response = await apiClient.get("/projects/?page_size=100&project_type=key_project");
          } else {
            // 项目类型未确定，不获取项目列表
            console.log("Project type not determined, skipping project fetch");
            return;
          }
          console.log("Projects response:", response);
          if (response.success && response.data) {
            setProjects(response.data);
            console.log(
              "Navbar - Fetched projects successfully:",
              response.data,
            ); // 新增日志
            console.log("Navbar - Total projects:", response.total); // 新增日志
            console.log("Navbar - Page info:", {
              page: response.page,
              page_size: response.page_size,
              total_pages: response.total_pages,
            }); // 新增日志

            // 检查项目中的用户信息
            response.data.forEach((project: any) => {
              console.log(
                `Navbar - Project ${project.id}: user_id=${project.user_id}, user=`,
                project.user,
              );
            });

            // 项目数据加载完成后，重新从URL设置selectedProject
            const regex = /^\/projects\/(\d+)/;
            const match = location.pathname.match(regex);
            if (match) {
              setSelectedProject(match[1]);
              console.log("Selected project from URL:", match[1]); // 新增日志
            }
          } else {
            console.error("Failed to fetch projects:", response.message);
          }
        } catch (error) {
          console.error("Error fetching projects:", error);
        }
      } else {
        console.log("User not logged in, skipping project fetch"); // 新增日志
      }
    };

    fetchProjects();

    // 监听全局事件，收到后刷新项目列表
    const handleRefresh = () => fetchProjects();
    window.addEventListener("projectListShouldRefresh", handleRefresh);
    return () => {
      window.removeEventListener("projectListShouldRefresh", handleRefresh);
    };
  }, [user, location.pathname]);


  useEffect(() => {
    const regex = /^\/projects\/(\d+)/;
    const match = location.pathname.match(regex);
    if (match) {
      const projectId = match[1];
      setSelectedProject(projectId);
      // Removed automatic redirect - let /projects/:projectId show the WorkspacePage
    } else {
      setSelectedProject("");
    }
  }, [location.pathname, navigate, projects]);

  const handleProjectChange = (event: any) => {
    const newProjectId = event.target.value;
    setSelectedProject(newProjectId);
    if (newProjectId) {
      // 查找项目类型，根据类型决定跳转到哪里
      const project = projects.find(p => p.id === parseInt(newProjectId));
      if (project?.project_type === 'performance_target') {
        navigate(`/projects/${newProjectId}/performance-indicators`);
      } else {
        navigate(`/projects/${newProjectId}`);
      }
    } else {
      navigate("/"); // Navigate to homepage if no project selected
    }
  };

  // 处理操作说明菜单
  const handleHelpMenuOpen = (event: any) => {
    setHelpMenuAnchor(event.currentTarget);
  };

  const handleHelpMenuClose = () => {
    setHelpMenuAnchor(null);
  };

  // 下载操作手册
  const handleDownloadManual = () => {
    window.open("/api/download/all-docs", "_blank");
    handleHelpMenuClose();
  };

  
  // 关闭提示
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };


  const navTabs = [
    {
      label: "AI探索",
      value: `/projects/${finalProjectId}`,
      visible: true,
    },
    {
      label: currentProjectType === "performance_target" 
        ? "绩效指标管理" 
        : currentProjectType === "department_overall"
        ? "部门指标管理"
        : currentProjectType === "monitoring_target"
        ? "事中监控指标管理"
        : "项目指标管理",
      value: currentProjectType === "performance_target"
        ? `/projects/${finalProjectId}/performance-indicators`
        : currentProjectType === "department_overall"
        ? `/projects/${finalProjectId}/department-indicators`
        : currentProjectType === "monitoring_target"
        ? `/projects/${finalProjectId}/monitoring-indicators`
        : `/projects/${finalProjectId}/indicators`,
      visible: !isArchived && currentProjectType !== "", // 只有在项目类型确定后才显示
    },
    {
      label: "章节写作",
      value: `/projects/${currentProjectId}/writing`,
      visible: !isArchived && currentProjectType === "key_project", // 只有重点项目显示
    },
  ];

  const visibleTabs = navTabs.filter((tab) => tab.visible);

  // 添加调试日志
  console.log("Navbar render - selectedProject:", selectedProject);
  console.log("Navbar render - projects.length:", projects.length);
  console.log("Navbar render - currentProject:", currentProject);
  console.log("Navbar render - isProjectDetailPage:", isProjectDetailPage);
  console.log("Navbar render - projectMenuItems.length:", projectMenuItems.length);
  console.log("Navbar render - currentProjectIdFromPath:", currentProjectIdFromPath);
  console.log("Navbar render - finalProjectId:", finalProjectId);
  console.log("Navbar render - navTabs values:", navTabs.map(tab => tab.value));

  return (
    <Box>
      <AppBar
        position="static"
        elevation={0}
        color="primary"
        sx={{
          borderBottom: '1px solid #e0e0e0',
          bgcolor: 'primary.main',
          transition: 'background-color 0.3s, border-color 0.3s'
        }}
        style={{ borderRadius: 0, zIndex: 99000 }}
      >
        <Toolbar sx={{ minHeight: "64px" }} style={{ zIndex: 99000 }}>
          {/* 左侧Logo和项目选择 */}
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mr: 2,
                cursor: "pointer",
                "&:hover": { opacity: 0.9 },
              }}
              onClick={() => {
                navigate("/");
              }}
            >
              <Box
                component="img"
                src="/imgs/logo.svg"
                alt="Tsl Rag Logo"
                sx={{
                  width: 28,
                  height: 28,
                  mr: 1.5,
                }}
              />
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 600,
                  letterSpacing: "-0.5px",
                }}
                style={{ fontSize: '1.1rem' }}
                color="white"
              >
                昭元绩效AI平台
              </Typography>
            </Box>

            {isProjectDetailPage && (
              <FormControl size="small" disabled={isArchived} sx={{
                minWidth: 200,
                maxWidth: 350,
                ml: 2
              }} >
                <InputLabel id="project-select-label">当前项目</InputLabel>
                <Select
                  labelId="project-select-label"
                  value={(() => {
                    // 确保选中的项目ID在项目列表中存在，否则设为空字符串
                    if (!selectedProject) return '';
                    const isValidProject = isArchived
                      ? currentProject?.id.toString() === selectedProject
                      : projects.some(p => p.id.toString() === selectedProject);
                    return isValidProject ? selectedProject : '';
                  })()}
                  label="当前项目"
                  onChange={handleProjectChange}
                  renderValue={(value) => {
                    // 自定义显示：显示项目名称而不是ID
                    const project = currentProject || projects.find((p: Project) => p.id.toString() === value);
                    if (project) {
                      return truncateProjectName(project.project_code, project.project_name);
                    }
                    return '';
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: 'background.paper',
                        color: 'inherit',
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {projectMenuItems}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* 中间的功能模块导航 */}
          {isProjectDetailPage && (
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                justifyContent: "center",
                minWidth: 0, // 允许收缩
                overflow: "hidden", // 防止内容溢出
              }}
            >
              <Tabs
                value={location.pathname} // This will determine the active tab
                textColor="inherit"
                indicatorColor="secondary"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    minWidth: 80,
                    maxWidth: 120,
                    fontSize: '0.85rem',
                    transition: 'all 0.2s',
                    color: 'white',
                    opacity: 0.8,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    '&.Mui-selected': {
                      opacity: 1,
                      color: 'white',
                    },
                    "&:hover": {
                      opacity: 1,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'white',
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: 'white',
                  }
                }}
              >
                {visibleTabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                    component={Link}
                    to={tab.value}
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      '&.Mui-selected': {
                        color: 'white',
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          {/* 右侧操作区 */}
          <Box
            sx={{
              display: "flex",
              flexGrow: 0,
              alignItems: "center",
              gap: 1,
              ml: "auto",
              minWidth: "fit-content",
            }}
          >

            {/* 管理员后台任务入口 - 只有管理员可见 */}
            {user && user.role && ['super_admin'].includes(user.role) && (
              <Tooltip title="后台任务管理">
                <IconButton
                  size="small"
                  onClick={() => navigate('/admin/tasks')}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    p: 1,
                    borderRadius: '50%',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)'
                    }
                  }}
                >
                  <AssignmentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* 知识库管理 - 只有管理员可见 */}
            {user && user.role && ['super_admin'].includes(user.role) && (
              <Tooltip title="知识库管理">
                <IconButton
                  size="small"
                  onClick={() => navigate('/knowledge-base')}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    p: 1,
                    borderRadius: '50%',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)'
                    }
                  }}
                >
                  <PsychologyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* 用户头像 */}
            {user && (
              <UserAvatar />
            )}

            {/* 购买套餐入口 */}
            <Tooltip title="购买套餐">
              <IconButton
                size="small"
                onClick={() => {
                  try {
                    window.dispatchEvent(
                      new CustomEvent("showPaymentDialog", {
                        detail: { reason: "manual", message: "" },
                      }),
                    );
                  } catch (e) {
                    console.error("打开购买对话框失败:", e);
                  }
                }}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  p: 1,
                  borderRadius: '50%',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.25)'
                  }
                }}
              >
                <ShoppingCartOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* 操作说明菜单 */}
            <Tooltip title="操作说明">
              <IconButton
                size="small"
                onClick={handleHelpMenuOpen}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  p: 1,
                  borderRadius: '50%',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.25)'
                  }
                }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>

  
          </Box>
        </Toolbar>

        {/* 操作说明菜单 */}
        <Menu
          anchorEl={helpMenuAnchor}
          open={Boolean(helpMenuAnchor)}
          onClose={handleHelpMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleDownloadManual}>
            <ListItemIcon>
              <MenuBookIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="下载产品操作手册" />
          </MenuItem>
        </Menu>
      </AppBar>


      {/* 操作结果提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

          </Box>
  );
}